import { Trans } from "react-i18next";
import React, { useState } from "react";
import { Form, ButtonGroup, Button } from "react-bootstrap";
import "../../../shared/styles/eeg-controls.css";
import "./EEGControlPanel.css";

import { Montage } from "@features/Montage/types/montage.types";

interface EEGControlPanelProps {
  onMontageChange: (montageId: string) => void;
  onSensitivityChange: (sensitivity: number) => void;
  onTimeScaleChange: (timeScale: number) => void;
  onFilterChange: (filter: FilterSettings) => void;
  onPlayPause: () => void;
  onSpeedChange: (speed: number) => void;
  isPlaying: boolean;
  montages?: Montage[];
  selectedMontage?: Montage | null;
  selectedPerspective?: any;
}

interface FilterSettings {
  highPass: number;
  lowPass: number;
  notch: boolean;
}

const EEGControlPanel: React.FC<EEGControlPanelProps> = ({
  onMontageChange,
  onSensitivityChange,
  onTimeScaleChange,
  onFilterChange,
  onPlayPause,
  onSpeedChange,
  isPlaying,
  montages = [],
  selectedMontage = null,
  selectedPerspective = null,
}) => {
  const [montage, setMontage] = useState(selectedMontage?.montage_id || "");
  const [sensitivity, setSensitivity] = useState(7);
  const [timeScale, setTimeScale] = useState(10);
  const [highPass, setHighPass] = useState(0.5);
  const [lowPass, setLowPass] = useState(70);
  const [notchFilter, setNotchFilter] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const handleMontageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setMontage(value);
    onMontageChange(value);
  };

  const handleSensitivityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setSensitivity(value);
    onSensitivityChange(value);
  };

  const handleTimeScaleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value);
    setTimeScale(value);
    onTimeScaleChange(value);
  };

  const handleFilterChange = () => {
    onFilterChange({
      highPass,
      lowPass,
      notch: notchFilter,
    });
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    onSpeedChange(speed);
  };

  return (
    <div className="eeg-control-panel">
      <div className="control-section">
        {/* Montage Selector */}
        <div className="control-group">
          <label
            htmlFor="montage-select"
            className="control-label"
            data-testid="montage-select-label"
          >
            <i className="bi bi-diagram-3"></i> <Trans ns="features" i18nKey="ui.copy.7db11d819855" /></label>
          <Form.Select
            id="montage-select"
            size="sm"
            value={montage}
            onChange={handleMontageChange}
            className="control-select"
            data-testid="montage-select"
          >
            <option value="" disabled>
              {montages.length > 0
                ? "Select a montage..."
                : "No montages available"}
            </option>
            {montages.map((m) => (
              <option key={m.montage_id} value={m.montage_id}>
                {m.montage_name}
              </option>
            ))}
          </Form.Select>
          {selectedMontage && (
            <div className="text-muted mt-1 eeg-control-panel__montage-hint">
              {selectedMontage.montage_name} - {selectedMontage.description}
            </div>
          )}
        </div>

        {/* Sensitivity Control */}
        <div className="control-group">
          <label htmlFor="sensitivity-slider" className="control-label">
            <i className="bi bi-bar-chart-steps"></i> <Trans ns="features" i18nKey="ui.copy.58d005fa0e78" />{sensitivity}{" "}
            <Trans ns="features" i18nKey="ui.copy.73dc899cce25" /></label>
          <Form.Range
            id="sensitivity-slider"
            min={1}
            max={20}
            value={sensitivity}
            onChange={handleSensitivityChange}
          />

          <div className="mt-2">
            <label
              htmlFor="time-scale-select"
              className="control-label sr-only"
            >
              <Trans ns="features" i18nKey="ui.copy.dfa73ac0d42b" /></label>
            <Form.Select
              id="time-scale-select"
              size="sm"
              value={timeScale}
              onChange={handleTimeScaleChange}
              className="control-select"
            >
              <option value="5"><Trans ns="features" i18nKey="ui.copy.f148d46a71b2" /></option>
              <option value="10"><Trans ns="features" i18nKey="ui.copy.ec3776c8f607" /></option>
              <option value="15"><Trans ns="features" i18nKey="ui.copy.c245ebc708e0" /></option>
              <option value="20"><Trans ns="features" i18nKey="ui.copy.a4611fde1346" /></option>
              <option value="30"><Trans ns="features" i18nKey="ui.copy.c81591e40621" /></option>
              <option value="60"><Trans ns="features" i18nKey="ui.copy.21aa8a67c248" /></option>
            </Form.Select>
          </div>
        </div>
      </div>

      <div className="control-section">
        {/* Filter Settings */}
        <div className="control-group">
          <label className="control-label">
            <i className="bi bi-sliders"></i> <Trans ns="features" i18nKey="ui.copy.96e578211aa2" /></label>
          <div className="filter-controls">
            <div className="filter-input">
              <label><Trans ns="features" i18nKey="ui.copy.0013847a85ac" /></label>
              <Form.Control
                type="number"
                size="sm"
                value={highPass}
                onChange={(e) => setHighPass(parseFloat(e.target.value))}
                onBlur={handleFilterChange}
                step="0.1"
                min="0"
                max="5"
              />
              <span><Trans ns="features" i18nKey="ui.copy.97aaf5bb4065" /></span>
            </div>
            <div className="filter-input">
              <label><Trans ns="features" i18nKey="ui.copy.eee07e635dbb" /></label>
              <Form.Control
                type="number"
                size="sm"
                value={lowPass}
                onChange={(e) => setLowPass(parseFloat(e.target.value))}
                onBlur={handleFilterChange}
                step="1"
                min="30"
                max="200"
              />
              <span><Trans ns="features" i18nKey="ui.copy.97aaf5bb4065" /></span>
            </div>
            <div className="filter-checkbox">
              <Form.Check
                type="checkbox"
                label="60Hz Notch"
                checked={notchFilter}
                onChange={(e) => {
                  setNotchFilter(e.target.checked);
                  handleFilterChange();
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="control-section">
        {/* Playback Controls */}
        <div className="control-group">
          <label className="control-label">
            <i className="bi bi-play-circle"></i> <Trans ns="features" i18nKey="ui.copy.c8e308704911" /></label>
          <div className="playback-controls">
            <ButtonGroup size="sm">
              <Button
                variant={isPlaying ? "danger" : "success"}
                onClick={onPlayPause}
                className="playback-button"
              >
                <i className={`bi bi-${isPlaying ? "pause" : "play"}-fill`}></i>
                {isPlaying ? " Pause" : " Play"}
              </Button>
            </ButtonGroup>

            <div className="speed-controls">
              <label><Trans ns="features" i18nKey="ui.copy.063a83567f47" /></label>
              <ButtonGroup size="sm">
                <Button
                  variant={
                    playbackSpeed === 0.5 ? "primary" : "outline-primary"
                  }
                  onClick={() => handleSpeedChange(0.5)}
                >
                  <Trans ns="features" i18nKey="ui.copy.141b96c1a8f2" /></Button>
                <Button
                  variant={playbackSpeed === 1 ? "primary" : "outline-primary"}
                  onClick={() => handleSpeedChange(1)}
                >
                  <Trans ns="features" i18nKey="ui.copy.6fe62d0dd7db" /></Button>
                <Button
                  variant={playbackSpeed === 2 ? "primary" : "outline-primary"}
                  onClick={() => handleSpeedChange(2)}
                >
                  <Trans ns="features" i18nKey="ui.copy.8b48f47fb234" /></Button>
                <Button
                  variant={playbackSpeed === 4 ? "primary" : "outline-primary"}
                  onClick={() => handleSpeedChange(4)}
                >
                  <Trans ns="features" i18nKey="ui.copy.64f2c0d3c55d" /></Button>
              </ButtonGroup>
            </div>
          </div>
        </div>
      </div>

      {/* Recording Info Display */}
      <div className="recording-info">
        <div className="info-item">
          <span className="info-label"><Trans ns="features" i18nKey="ui.copy.0d616d6f0df9" /></span>
          <span className="info-value"><Trans ns="features" i18nKey="ui.copy.318bec41671c" /></span>
        </div>
        <div className="info-item">
          <span className="info-label"><Trans ns="features" i18nKey="ui.copy.9693aeaaf68e" /></span>
          <span className="info-value">60:00</span>
        </div>
        <div className="info-item">
          <span className="info-label"><Trans ns="features" i18nKey="ui.copy.b727b80213b5" /></span>
          <span className="info-value">19</span>
        </div>
      </div>
    </div>
  );
};

export default EEGControlPanel;
